import React, { useState } from "react";
import { Banner } from "../Banner";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Flex } from "../Flex";

import { userActions } from "../../redux/user/user.slice";
import { Text } from "../Text";
import { AtiraImage } from "../Image";
import { getMediaUrl } from "../../utils/Media";
import styled from "styled-components";
import { Rounded } from "../../theme/Rounded";
import { Spacing } from "../../theme/Spacing";
import { PSADescription } from "./PSADescription";
import { AtiraIcon } from "../AtiraIcon";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";
import { Carousel } from "../../utils/local-antd";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { Breakpoints } from "../../theme/Breakpoints";
import i18n from "../../i18n";

const StyledBanner = styled(Banner)`
  height: 6rem;
  & > div {
    box-shadow: none;
  }

  .ant-carousel {
    width: 100%;
    height: 100%;
    direction: inherit;
  }

  .slick-slider {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .slick-slider > svg {
    background-color: ${({ theme }) => theme.sub}cc;
    width: 0.75rem;
    height: 0.75rem;
    aspect-ratio: 1 / 1;
    padding: ${Spacing.s};
    border-radius: ${Rounded.circle};
    transition: all 0.3s;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    cursor: pointer;
  }

  .slick-slider > svg:first-of-type {
    inset-inline-end: ${Spacing.m};
  }

  .slick-slider > svg:last-of-type {
    inset-inline-start: ${Spacing.m};
  }

  .slick-slider > svg:hover {
    background-color: ${({ theme }) => theme.sub};
  }

  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: 100%;
  }

  .slick-arrow {
    opacity: 1;
    background-color: ${({ theme }) => theme.sub}cc;
    height: 1.75rem;
    width: 1.75rem;
    display: flex !important;
    align-items: center;
    justify-content: center;
    border-radius: ${Rounded.circle};
    transition: all 0.3s;

    &::after {
      display: none;
    }

    &:hover {
      background-color: ${({ theme }) => theme.sub} !important;
    }
  }

  .atira-banner-content-wrapper {
    background-color: ${({ theme }) => theme.darkMain};
  }
`;

const ImageWrapper = styled(Flex)`
  height: 5.25rem;
  aspect-ratio: 1 / 1;
  margin-block: ${Spacing.xs};

  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${Rounded.xl};
    border: ${({ theme }) => `2px solid ${theme.main}cc`};
  }
`;

const Container = styled(Flex)<{ loading: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
  opacity: ${({ loading }) => (loading ? 0.7 : 1)};

  html[dir="rtl"] & svg {
    transform: rotateY(180deg);
  }
`;

const Content = styled(Flex)`
  gap: ${Spacing.m};
  width: 100%;
  align-items: center;
  justify-content: center;
  height: 100%;
  direction: ${i18n.dir(i18n.language)};
`;

const TextContentWrapper = styled(Flex)`
  flex-direction: column;
  max-width: 60%;

  svg {
    width: 1.5rem;
  }

  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    max-width: 60%;
  }
`;

const BullhornIconWrapper = styled(Flex)`
  height: 100%;
  max-height: 5rem;
  aspect-ratio: 1 / 1;
  opacity: 0.2;
  transform: rotateY(180deg);

  html[dir="ltr"] & {
    rotate: 375deg;
  }

  html[dir="rtl"] & {
    rotate: 345deg;
  }

  svg {
    width: 80%;
    height: 80%;
    display: block;
    margin: auto;
  }
`;

export const PSAUnreadBanner: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const possibleLastUnreadPSAs =
    useAppSelector(userSliceSelectors.selectLastUnreadPSAs) || [];
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const dispatch = useAppDispatch();

  const onClose = async () => {
    if (possibleLastUnreadPSAs?.[currentSlide]) {
      setLoading(true);
      await dispatch(
        userActions.psaMarkAsRead({
          userId,
          psaId: possibleLastUnreadPSAs[currentSlide]?._id,
        }),
      ).unwrap();
      await dispatch(userActions.psaGetLastUnread({ userId })).unwrap();
      setLoading(false);
    }
  };

  if (!possibleLastUnreadPSAs?.length) {
    return null;
  }

  return (
    <StyledBanner open onClose={onClose} closeButtonDisabled={loading}>
      <Carousel
        initialSlide={0}
        arrows={possibleLastUnreadPSAs.length > 1}
        dots={false}
        infinite={false}
        nextArrow={
          <div>
            <AtiraIcon icon={faChevronRight} color="main" />
          </div>
        }
        prevArrow={
          <div>
            <AtiraIcon icon={faChevronLeft} color="main" />
          </div>
        }
        autoplay
        autoplaySpeed={10000}
        beforeChange={(current, next) => setCurrentSlide(next)}
      >
        {possibleLastUnreadPSAs.map((psa) => (
          <Container loading={loading}>
            <Content>
              {psa.images?.[0] ? (
                <ImageWrapper>
                  <AtiraImage src={getMediaUrl(psa.images[0])} />
                </ImageWrapper>
              ) : null}

              <TextContentWrapper>
                <Text fontWeight={"bold"} color="white">
                  {psa.title}
                </Text>
                {psa?.description ? <PSADescription psa={psa as any} /> : null}
              </TextContentWrapper>

              <BullhornIconWrapper>
                <AtiraIcon icon={faBullhorn} color="sub" />
              </BullhornIconWrapper>
            </Content>
          </Container>
        ))}
      </Carousel>
    </StyledBanner>
  );
};
